import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { ArrowLeft, Printer, Trophy, Plus, Minus, TrendingUp } from "lucide-react";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

/**
 * Print-optimized pitch deck for a saved synergy.
 * Layout: 1-page exec summary + selling points + pros/cons + projection.
 * URL: /app/pitch?key=firmA:firmB:productId
 * Calling window.print() (or hitting the toolbar "Print" button) produces a clean PDF.
 */
export default function Pitch() {
    const [params] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState("");

    const key = params.get("key");

    useEffect(() => {
        if (!key) { setError("Missing ?key= parameter"); setLoading(false); return; }
        (async () => {
            try {
                const { data } = await api.get(`/synergies/${encodeURIComponent(key)}`);
                setDoc(data);
                // Auto-trigger print dialog after a short delay so the layout settles
                if (params.get("auto") === "1") {
                    setTimeout(() => window.print(), 800);
                }
            } catch (e) {
                const d = e?.response?.data?.detail;
                setError(typeof d === "string" ? d : "Could not load synergy");
            } finally {
                setLoading(false);
            }
        })();
    }, [key, params]);

    if (loading) return <div className="p-12"><Loader label="Loading pitch" /></div>;
    if (error) return <div className="p-12"><div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono">{error}</div></div>;
    if (!doc) return null;

    const a = doc.analysis || {};
    const sellingPoints = a.selling_points || [];
    const pros = (a.pros_cons?.pros) || [];
    const cons = (a.pros_cons?.cons) || [];
    const projection = a.monthly_revenue_projection || [];
    const product = a.product || { name: doc.product_name };
    const fit = a.fit_score || doc.fit_score || 0;
    const revenue = a.estimated_added_annual_revenue_usd || doc.projected_revenue || 0;

    return (
        <div className="p-8 max-w-4xl mx-auto" data-testid="pitch-page">
            <div className="print:hidden mb-6 flex items-center justify-between">
                <Link to={`/app/synergy?key=${encodeURIComponent(key)}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-amber-500">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to synergy
                </Link>
                <button
                    onClick={() => window.print()}
                    data-testid="pitch-print-btn"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-3 py-2"
                >
                    <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                </button>
            </div>

            {/* Cover */}
            <div className="border border-zinc-800 bg-zinc-950 p-8 mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Partnership pitch</div>
                <h1 className="font-heading font-black tracking-tighter text-5xl text-zinc-50 mb-3 leading-none">
                    {doc.firm_a_name} <span className="text-amber-500">×</span> {doc.firm_b_name}
                </h1>
                <p className="text-lg text-zinc-300 mt-4 leading-relaxed">{a.rationale || "—"}</p>

                <div className="grid grid-cols-3 gap-px bg-zinc-800 mt-8">
                    <div className="bg-zinc-950 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Fit score</div>
                        <div className="font-heading font-black text-4xl text-amber-500">{fit}<span className="text-xl text-zinc-600">/100</span></div>
                    </div>
                    <div className="bg-zinc-950 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Projected revenue</div>
                        <div className="font-heading font-black text-4xl text-emerald-400">{fmtMoney(revenue)}<span className="text-xs text-zinc-500 ml-1">/yr</span></div>
                    </div>
                    <div className="bg-zinc-950 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Product</div>
                        <div className="font-heading font-bold text-lg text-zinc-50 leading-tight">{product.name || "—"}</div>
                    </div>
                </div>
            </div>

            {/* Selling points */}
            {sellingPoints.length > 0 && (
                <section className="border border-zinc-800 bg-zinc-950 mb-6" data-testid="pitch-selling-points">
                    <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Why {doc.firm_b_name} should care</div>
                    </div>
                    <ol className="divide-y divide-zinc-800/60">
                        {sellingPoints.map((sp, i) => (
                            <li key={i} className="flex gap-4 px-5 py-4">
                                <div className="font-mono text-xs text-amber-500 w-6 shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</div>
                                <div className="min-w-0">
                                    <div className="font-heading font-bold text-zinc-50 text-base mb-1">{sp.point}</div>
                                    <div className="text-sm text-zinc-300 leading-relaxed">{sp.detail}</div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {/* Pros / Cons */}
            {(pros.length > 0 || cons.length > 0) && (
                <section className="grid grid-cols-2 gap-px bg-zinc-800 mb-6 border border-zinc-800" data-testid="pitch-pros-cons">
                    <div className="bg-zinc-950 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Plus className="w-4 h-4 text-emerald-400" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">Pluses</div>
                        </div>
                        <ul className="space-y-2">
                            {pros.map((p, i) => (
                                <li key={i} className="text-sm text-zinc-200 leading-relaxed flex gap-2"><Plus className="w-3 h-3 text-emerald-400 mt-1 shrink-0" />{p}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-zinc-950 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Minus className="w-4 h-4 text-red-400" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">Minuses</div>
                        </div>
                        <ul className="space-y-2">
                            {cons.map((c, i) => (
                                <li key={i} className="text-sm text-zinc-200 leading-relaxed flex gap-2"><Minus className="w-3 h-3 text-red-400 mt-1 shrink-0" />{c}</li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* Projection table (print-friendly, no chart) */}
            {projection.length > 0 && (
                <section className="border border-zinc-800 bg-zinc-950" data-testid="pitch-projection">
                    <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-amber-500" />
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">12-month revenue projection</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-zinc-800 text-left">
                                    <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Month</th>
                                    <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projection.map((m, i) => (
                                    <tr key={i} className="border-b border-zinc-800/60">
                                        <td className="px-5 py-2 font-mono text-xs text-zinc-300">{m.month}</td>
                                        <td className="px-5 py-2 font-mono text-xs text-amber-500 text-right">{fmtMoney(m.revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="px-5 py-2 font-mono text-xs text-zinc-50 font-bold">Total</td>
                                    <td className="px-5 py-2 font-mono text-sm text-emerald-400 text-right font-bold">{fmtMoney(projection.reduce((acc, m) => acc + (Number(m.revenue) || 0), 0))}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </section>
            )}

            <div className="print:hidden mt-6 text-center font-mono text-[10px] text-zinc-600">
                Tip: in the print dialog, choose "Save as PDF" to share this pitch with stakeholders.
            </div>
        </div>
    );
}
